import { Button } from '@lms/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@lms/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@lms/ui/popover';
import { cn } from '@lms/ui/utils';
import { Check, ChevronsUpDown } from 'lucide-react';
import * as React from 'react';

function compare(searchString: string, text: string): boolean {
  if (!searchString) return true;
  const terms = searchString.toLowerCase().split(/\s+/);
  const targetText = text.toLowerCase();

  // Normalize both strings to remove diacritics
  const normalize = (str: string) => str.normalize('NFD').replace(/[̀-ͯ]/g, '');

  return terms.every((term) => normalize(targetText).includes(normalize(term)));
}

export function GroupSelector({
  groups,
  placeholder,
  emptyString,
  value,
  onChange,
}: {
  groups: { label: string; value: string }[];
  placeholder: string;
  emptyString: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [listItems, setListItems] = React.useState(groups);
  const [search, setSearch] = React.useState('');

  React.useEffect(() => {
    if (search.trim().length > 0) {
      setListItems(groups.filter(({ label }) => compare(search, label)));
    } else {
      setListItems(groups);
    }
  }, [search, groups]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[350px] justify-between"
          data-testid="group-selector-trigger"
        >
          {value
            ? groups.find((group) => group.value === value)?.label
            : placeholder}
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[350px] p-0">
        <Command shouldFilter={false}>
          <CommandInput placeholder={placeholder} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>{emptyString}</CommandEmpty>
            <CommandGroup>
              {listItems.map((group) => (
                <CommandItem
                  key={group.value}
                  value={group.value}
                  onSelect={() => {
                    onChange(group.value === value ? '' : group.value);
                    setOpen(false);
                  }}
                >
                  {group.label}
                  <Check
                    className={cn(
                      'ml-auto',
                      value === group.value ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
